import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Bar, ComposedChart,
} from "recharts";
import { ChartLine } from "@phosphor-icons/react";

const fmtEUR = (n) => (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const monthLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  return `${MONTHS_ES[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

export default function MonthlyChart() {
  const [temporada, setTemporada] = useState("");
  const [data, setData] = useState({ series: [], temporadas: [] });

  useEffect(() => {
    (async () => {
      try {
        const params = temporada ? { temporada } : {};
        const { data } = await api.get("/transactions/monthly", { params });
        setData(data);
      } catch { /* ignore */ }
    })();
  }, [temporada]);

  const series = data.series || [];
  const temporadas = data.temporadas || [];

  const totals = useMemo(() => {
    return series.reduce(
      (acc, s) => ({
        ingresos: acc.ingresos + s.ingresos,
        gastos: acc.gastos + s.gastos,
      }),
      { ingresos: 0, gastos: 0 }
    );
  }, [series]);
  const beneficioAcumulado = totals.ingresos - totals.gastos;

  const chartData = series.map((s) => ({
    ...s,
    label: monthLabel(s.month),
  }));

  return (
    <div className="bg-white border border-slate-200 p-5 shadow-card" style={{ borderRadius: 12 }} data-testid="monthly-chart-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ChartLine size={20} className="text-[#2563EB]" weight="bold" />
          <h3 className="font-display font-bold text-xl uppercase tracking-tight">Evolución mensual</h3>
        </div>
        <select
          data-testid="monthly-temporada"
          value={temporada}
          onChange={(e) => setTemporada(e.target.value)}
          className="bg-white border border-slate-200 px-3 h-9 text-sm text-slate-900 focus:border-[#2563EB] outline-none"
          style={{ borderRadius: 6 }}
        >
          <option value="">Todas las temporadas</option>
          {temporadas.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Mini KPIs de la serie visible */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <MiniKPI label="Ingresos" value={fmtEUR(totals.ingresos)} color="#22C55E" testid="monthly-total-ingresos" />
        <MiniKPI label="Gastos" value={fmtEUR(totals.gastos)} color="#EF4444" testid="monthly-total-gastos" />
        <MiniKPI
          label="Beneficio"
          value={fmtEUR(beneficioAcumulado)}
          color={beneficioAcumulado >= 0 ? "#2563EB" : "#EF4444"}
          testid="monthly-total-beneficio"
        />
      </div>

      {chartData.length === 0 ? (
        <div className="text-center text-slate-500 text-sm py-16">Sin movimientos para la temporada seleccionada</div>
      ) : (
        <div className="h-72" data-testid="monthly-chart-container">
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, color: "#0F172A", fontSize: 12 }}
                formatter={(v) => fmtEUR(v)}
              />
              <Legend />
              <Bar dataKey="ingresos" name="Ingresos" fill="#22C55E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="gastos" name="Gastos" fill="#EF4444" radius={[4, 4, 0, 0]} />
              <Line
                type="monotone"
                dataKey="beneficio"
                name="Beneficio"
                stroke="#2563EB"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#2563EB" }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function MiniKPI({ label, value, color, testid }) {
  return (
    <div className="border border-slate-200 p-3" style={{ borderRadius: 8 }} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="font-display font-bold text-xl mt-1 tracking-tighter" style={{ color }}>{value}</div>
    </div>
  );
}
